import { useCallback } from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
// Api
import { GET_TICKET_EVENT } from 'api/ticketing/queries';
import { SET_TICKET_SALE_STATUS } from 'api/ticketing/mutations';
// Types
import {
  GetTicketEvent,
  GetTicketEventVariables,
} from 'api/ticketing/types/GetTicketEvent';
import {
  SetTicketSaleStatus,
  SetTicketSaleStatusVariables,
} from 'api/ticketing/types/SetTicketSaleStatus';
import { TicketEventDetailsFields } from 'api/ticketing/types/TicketEventDetailsFields';
import { TicketEventStatus } from 'api/graphql-global-types';
// Helpers
import {
  getAddTierAvailability,
  getTicketingErrorMessage,
} from 'pages/Events/helpers';
// UI
import { errorNotification } from 'ui/Notification';

type PausableEvent = Pick<
  TicketEventDetailsFields,
  'id' | 'status' | 'version'
>;

export type WithSalesPaused = <T>(
  event: PausableEvent,
  change: () => Promise<T>
) => Promise<T>;

/** Runs a tier change with sales paused while the event is live, and always tries to reopen them. */
export const useWithSalesPaused = (): WithSalesPaused => {
  const client = useApolloClient();
  const [setSaleStatus] = useMutation<
    SetTicketSaleStatus,
    SetTicketSaleStatusVariables
  >(SET_TICKET_SALE_STATUS);

  const reopenSales = useCallback(
    async (ticketEventId: string): Promise<void> => {
      const { data } = await client.query<
        GetTicketEvent,
        GetTicketEventVariables
      >({
        query: GET_TICKET_EVENT,
        variables: { input: { ticketEventId } },
        fetchPolicy: 'network-only',
      });
      const latest = data?.hostedTicketEvent;

      // Someone may have moved the event on in the meantime
      if (latest?.status !== TicketEventStatus.Paused) {
        return;
      }

      await setSaleStatus({
        variables: {
          input: {
            ticketEventId,
            expectedVersion: latest.version,
            status: TicketEventStatus.OnSale,
          },
        },
      });
    },
    [client, setSaleStatus]
  );

  return useCallback<WithSalesPaused>(
    async (event, change) => {
      if (!getAddTierAvailability(event.status).pausesSales) {
        return change();
      }

      await setSaleStatus({
        variables: {
          input: {
            ticketEventId: event.id,
            expectedVersion: event.version,
            status: TicketEventStatus.Paused,
          },
        },
      });

      try {
        return await change();
      } finally {
        await reopenSales(event.id).catch((error) =>
          errorNotification(
            getTicketingErrorMessage(
              error,
              'Sales are still paused: resume them from the event page'
            )
          )
        );
      }
    },
    [reopenSales, setSaleStatus]
  );
};
